import type { Answer, Question } from './types';
import { BUDGET_BUCKETS } from './schema';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Returns a short human-language acknowledgment of the user's answer.
 * Layer 3 JTBD: user feels the product understands them, not just records data.
 *
 * Returns null when there is nothing meaningful to say (empty answer, no key defined).
 */
export function getSomaticEcho(
  question: Question,
  answer: Answer | undefined,
  t: TFn,
): string | null {
  if (!answer) return null;

  if (question.type === 'budget-slider') {
    const bucket = BUDGET_BUCKETS.find((b) => b.id === answer.budgetBucket);
    if (!bucket) return null;
    const key = `quiz.echo.budget.${bucket.id}`;
    const msg = t(key, { defaultValue: '' });
    return msg || null;
  }

  const ids = answer.optionIds;
  if (!ids || ids.length === 0) return null;

  const base = `quiz.echo.${question.id}`;

  // Ties is a country multi-select: option ids are country codes (IT, PT…),
  // not semantic keys. Use _single / _multi echo keys regardless of count
  // to avoid 30 per-country echo entries.
  if (question.id === 'ties') {
    const key = ids.length === 1 ? `${base}._single` : `${base}._multi`;
    const msg = t(key, { count: ids.length, defaultValue: '' });
    return msg || null;
  }

  // Single selection or first of multi — try specific key
  if (ids.length === 1) {
    const key = `${base}.${ids[0]}`;
    const msg = t(key, { defaultValue: '' });
    return msg || null;
  }

  // Multiple selections — try _multi key with count
  const multiKey = `${base}._multi`;
  const msg = t(multiKey, { count: ids.length, defaultValue: '' });
  return msg || null;
}
